import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import i18next from 'i18next';
import { action, makeObservable, observable } from 'mobx';

import { OrderRequests } from '~/api/Order';
import { ApiErrorResponse } from '~/api/Requests';
import { company } from '~/company/Company';
import { checkoutStore, mainStore, orderStore, userStore } from '~/stores';
import {
  validateCardNumber,
  validateCVV,
  validateExpiryDate,
} from '~/utils/cardValidators';

export interface CardData {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

export interface EncryptedCardDetails {
  encryptedCard: string;
  bin: string;
  last4Digits: string;
  expiryMonth: string;
  expiryYear: string;
}

declare global {
  interface Window {
    Cardinal?: {
      configure: (config: Record<string, unknown>) => void;
      on: (event: string, callback: (data: any, jwt?: string) => void) => void;
      setup: (action: string, options: { jwt: string }) => void;
      continue: (
        action: string,
        data: Record<string, unknown>,
        options: Record<string, unknown>,
      ) => void;
      trigger: (event: string, data: unknown) => void;
    };
    verifone?: {
      encryptCard: (card: CardData, encryptionKey: string) => Promise<string>;
      getEncryptedCardDetails: (
        card: CardData,
        encryptionKey: string,
      ) => Promise<EncryptedCardDetails>;
    };
  }
}

class VerifoneService {
  private sessionId = '';
  private encryptedCard = '';
  private payment_Id = '';
  private order_Id = '';
  public validationError: string | null = null;

  constructor() {
    makeObservable(this, {
      validationError: observable,
      clearValidationError: action,
    });
  }

  private verifoneScriptLoaded = false;
  private cardinalScriptLoaded = false;
  private cardinalConfigured = false;

  setCardBrand(cardBrand: string) {
    Sentry.addBreadcrumb({
      category: 'verifone',
      message: 'Card brand set',
      level: 'info',
      data: {
        cardBrand,
      },
    });
  }

  setEncryptedCard(encryptedCard: string) {
    this.encryptedCard = encryptedCard;
  }

  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
  }

  get paymentId() {
    return this.payment_Id;
  }

  get orderId() {
    return this.order_Id;
  }

  get getSessionId() {
    return this.sessionId;
  }

  get getEncryptedCard() {
    return this.encryptedCard;
  }

  clearValidationError() {
    this.validationError = null;
  }

  async createJwt() {
    try {
      Sentry.addBreadcrumb({
        category: 'verifone',
        message: 'Creating JWT for 3DS authentication',
        level: 'info',
      });

      const response = await OrderRequests.verifone3ds();

      Sentry.addBreadcrumb({
        category: 'verifone',
        message: 'JWT created successfully',
        level: 'info',
        data: {
          hasJwt: !!response.jwt,
        },
      });

      return response.jwt;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'VerifoneService',
          method: 'createJwt',
        },
        extra: {
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
      throw error;
    }
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      if (
        (src.indexOf('verifone.js') !== -1 &&
          typeof window !== 'undefined' &&
          window.verifone) ||
        (src.indexOf('songbird') !== -1 &&
          typeof window !== 'undefined' &&
          window.Cardinal)
      ) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  configureAndSetupCardinal() {
    try {
      if (this.cardinalConfigured) {
        Sentry.addBreadcrumb({
          category: 'verifone',
          message: 'Cardinal already configured, skipping',
          level: 'info',
        });
        return;
      }

      if (window && window.Cardinal) {
        this.cardinalConfigured = true;

        Sentry.addBreadcrumb({
          category: 'verifone',
          message: 'Configuring Cardinal Commerce',
          level: 'info',
        });

        window.Cardinal.on(
          'payments.setupComplete',
          async (setupCompleteData) => {
            try {
              Sentry.addBreadcrumb({
                category: 'verifone',
                message: 'Cardinal setup complete',
                level: 'info',
                data: {
                  sessionId: setupCompleteData.sessionId,
                },
              });

              verifoneService.setSessionId(setupCompleteData.sessionId);

              await verifoneService.createTransaction();
            } catch (error) {
              Sentry.captureException(error, {
                tags: {
                  component: 'VerifoneService',
                  method: 'configureAndSetupCardinal.setupComplete',
                },
                extra: {
                  sessionId: setupCompleteData.sessionId,
                  error:
                    error instanceof Error ? error.message : 'Unknown error',
                },
              });
              console.error('Error in Cardinal setupComplete:', error);
            }
          },
        );

        window.Cardinal.on('payments.validated', async function (data) {
          try {
            Sentry.addBreadcrumb({
              category: 'verifone',
              message: 'Cardinal payment validated',
              level: 'info',
              data: {
                actionCode: data.ActionCode,
                payment_Id: verifoneService.paymentId,
              },
            });

            switch (data.ActionCode) {
              case 'SUCCESS':
                {
                  Sentry.addBreadcrumb({
                    category: 'verifone',
                    message: '3DS authentication successful',
                    level: 'info',
                  });

                  try {
                    await OrderRequests.verifone3dsConfirm({
                      payment_id: verifoneService.paymentId,
                      details: {
                        encryptedCard: verifoneService.encryptedCard,
                        threedAuthentication: data.Payment.ExtendedData,
                      },
                    });
                    await checkoutStore.finalizePayment();
                  } catch {
                    checkoutStore.setIsLoading(false);
                    orderStore.setIsLoading(false);
                    mainStore.pushAlert('error', 'Payment failed');
                  }
                }
                break;
              default: {
                checkoutStore.setIsLoading(false);
                Sentry.captureMessage('3DS authentication failed', {
                  level: 'error',
                  tags: {
                    component: 'VerifoneService',
                    method: 'configureAndSetupCardinal.validated',
                  },
                  extra: {
                    actionCode: data.ActionCode,
                    payment_Id: verifoneService.paymentId,
                  },
                });
                mainStore.pushAlert(
                  'error',
                  i18next.t('errors:3dsVerificationFailed'),
                );
                await orderStore
                  .orderCancel(verifoneService.orderId)
                  .catch((error) => error && console.error(error));
              }
            }
          } catch (error) {
            Sentry.captureException(error, {
              tags: {
                component: 'VerifoneService',
                method: 'configureAndSetupCardinal.validated',
              },
              extra: {
                actionCode: data.ActionCode,
                error: error instanceof Error ? error.message : 'Unknown error',
              },
            });
            console.error('Error in Cardinal validated:', error);
          }
        });
      } else {
        Sentry.captureMessage('Cardinal not available for configuration', {
          level: 'warning',
          tags: {
            component: 'VerifoneService',
            method: 'configureAndSetupCardinal',
          },
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'VerifoneService',
          method: 'configureAndSetupCardinal',
        },
        extra: {
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
      console.error('Error configuring Cardinal:', error);
    }
  }

  async createTransaction() {
    try {
      Sentry.addBreadcrumb({
        category: 'verifone',
        message: 'Creating transaction',
        level: 'info',
        data: {
          hasEncryptedCard: !!this.encryptedCard,
          hasSessionId: !!this.sessionId,
        },
      });
      const response = await checkoutStore.orderCheckout(null, null, {
        paymentMethodInfo: {
          encryptedCard: this.encryptedCard,
          deviceInfoId: this.sessionId,
        },
        billingInfo: {
          firstName: userStore.personalData.firstName || '',
          lastName: userStore.personalData.lastName || '',
          address: `${userStore.deliveryAddress?.city} ${userStore.deliveryAddress?.addressBld} ${userStore.deliveryAddress?.addressApt}`,
          city: userStore.deliveryAddress?.city || '',
          countryCode: company.config.countryCode || '',
        },
      });

      const resp =
        response?.data.orders[0]?.payment?.providerPaymentDetails
          ?.additionalData;

      this.payment_Id = response?.data.orders[0]?.payment.id || '';
      this.order_Id = response?.data.orders[0]?.order.id || '';

      Sentry.addBreadcrumb({
        category: 'verifone',
        message: 'Transaction created',
        level: 'info',
        data: {
          orderId: this.orderId,
          hasAcsUrl: !!resp?.acs_url,
          hasPayload: !!resp?.payload,
          hasTransactionId: !!resp?.transaction_id,
        },
      });

      if (
        resp?.acs_url &&
        resp.payload &&
        resp.transaction_id &&
        typeof window !== 'undefined' &&
        window.Cardinal
      ) {
        Sentry.addBreadcrumb({
          category: 'verifone',
          message: 'Starting 3DS challenge',
          level: 'info',
          data: {
            acsUrl: resp.acs_url,
            transactionId: resp.transaction_id,
          },
        });

        window.Cardinal.continue(
          'cca',
          {
            AcsUrl: resp.acs_url,
            Payload: resp.payload,
          },
          {
            OrderDetails: {
              TransactionId: resp.transaction_id,
            },
          },
        );
      } else {
        Sentry.addBreadcrumb({
          category: 'verifone',
          message: 'Skipping 3DS, finalizing payment',
          level: 'info',
        });
        checkoutStore.finalizePayment();
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'VerifoneService',
          method: 'createTransaction',
        },
        extra: {
          orderId: this.payment_Id,
          hasEncryptedCard: !!this.encryptedCard,
          hasSessionId: !!this.sessionId,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
      const errorInstance = error as AxiosError<ApiErrorResponse>;
      mainStore.pushAlert('error', errorInstance.message);
      throw error;
    }
  }

  loadScripts(): Promise<boolean> {
    try {
      Sentry.addBreadcrumb({
        category: 'verifone',
        message: 'Loading Verifone scripts',
        level: 'info',
        data: {
          verifoneScriptLoaded: this.verifoneScriptLoaded,
          cardinalScriptLoaded: this.cardinalScriptLoaded,
        },
      });

      if (typeof window !== 'undefined' && window.verifone) {
        this.verifoneScriptLoaded = true;
      }
      if (typeof window !== 'undefined' && window.Cardinal) {
        this.cardinalScriptLoaded = true;
      }

      const loaders: Promise<void>[] = [];

      if (!this.verifoneScriptLoaded) {
        loaders.push(
          this.loadScript(
            company.envVariables.REACT_APP_VERIFONE_SCRIPT_URL,
          ).then(() => {
            this.verifoneScriptLoaded = true;
            Sentry.addBreadcrumb({
              category: 'verifone',
              message: 'Verifone script loaded',
              level: 'info',
            });
          }),
        );
      }
      if (!this.cardinalScriptLoaded) {
        loaders.push(
          this.loadScript(
            company.envVariables.REACT_APP_CARDINAL_SCRIPT_URL,
          ).then(() => {
            this.cardinalScriptLoaded = true;
            Sentry.addBreadcrumb({
              category: 'verifone',
              message: 'Cardinal script loaded',
              level: 'info',
            });
          }),
        );
      }

      const waitAll = loaders.length ? Promise.all(loaders) : Promise.resolve();

      return waitAll.then(() => {
        this.configureAndSetupCardinal();
        return true;
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'VerifoneService',
          method: 'loadScripts',
        },
        extra: {
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
      throw error;
    }
  }

  async initializePayment(): Promise<void> {
    try {
      Sentry.addBreadcrumb({
        category: 'verifone',
        message: 'Starting card encryption',
        level: 'info',
      });

      this.clearValidationError();
      checkoutStore.setIsLoading(true);

      if (window && window.verifone) {
        const cardNumberValue =
          (document.getElementById('cardNumber') as HTMLInputElement)?.value ||
          '';
        const expiryDateValue =
          (document.getElementById('expiryDate') as HTMLInputElement)?.value ||
          '';
        const cvvValue =
          (document.getElementById('cvv') as HTMLInputElement)?.value || '';

        if (!validateCardNumber(cardNumberValue)) {
          this.validationError = 'Invalid card number';
          checkoutStore.setIsLoading(false);
          Sentry.addBreadcrumb({
            category: 'verifone',
            message: 'Validation failed: Invalid card number',
            level: 'warning',
          });
          return;
        }

        if (!validateExpiryDate(expiryDateValue)) {
          this.validationError = 'Invalid expiry date';
          checkoutStore.setIsLoading(false);
          Sentry.addBreadcrumb({
            category: 'verifone',
            message: 'Validation failed: Invalid expiry date',
            level: 'warning',
          });
          return;
        }

        if (!validateCVV(cvvValue)) {
          this.validationError = 'Invalid CVV';
          checkoutStore.setIsLoading(false);
          Sentry.addBreadcrumb({
            category: 'verifone',
            message: 'Validation failed: Invalid CVV',
            level: 'warning',
          });
          return;
        }

        const [expiryMonth, expiryYear] = expiryDateValue.split('/');

        Sentry.addBreadcrumb({
          category: 'verifone',
          message: 'Card data validated and retrieved from form',
          level: 'info',
          data: {
            hasCardNumber: !!cardNumberValue,
            hasExpiryDate: !!expiryDateValue,
            hasCvv: !!cvvValue,
            cardNumberLength: cardNumberValue.length,
            expiryMonth,
            expiryYear,
          },
        });

        const cardData: CardData = {
          cardNumber: cardNumberValue.replace(/\s/g, ''),
          expiryMonth: expiryMonth?.padStart(2, '0') || '',
          expiryYear: expiryYear || '',
          cvv: cvvValue,
        };

        const encryptedCard = await window.verifone.encryptCard(
          cardData,
          company.envVariables.REACT_APP_ENCRYPTION_KEY,
        );

        Sentry.addBreadcrumb({
          category: 'verifone',
          message: 'Card encrypted successfully',
          level: 'info',
          data: {
            hasEncryptedCard: !!encryptedCard,
          },
        });

        verifoneService.setEncryptedCard(encryptedCard);

        const jwt = await verifoneService.createJwt();

        if (jwt) {
          Sentry.addBreadcrumb({
            category: 'verifone',
            message: 'Setting up Cardinal with JWT',
            level: 'info',
          });

          window.Cardinal?.setup('init', {
            jwt,
          });
        } else {
          this.createTransaction();
        }

        return;
      } else {
        Sentry.captureMessage('Verifone not available for card encryption', {
          level: 'error',
          tags: {
            component: 'VerifoneService',
            method: 'ecryptCard',
          },
        });
        throw new Error('Verifone not available');
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'VerifoneService',
          method: 'ecryptCard',
        },
        extra: {
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
      checkoutStore.setIsLoading(false);
      throw error;
    }
  }
}

export const verifoneService = new VerifoneService();
